import React from 'react';
import { observer } from 'mobx-react';
import { SvgIcon, Box } from 'components';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { Warning, Check } from 'icons';
import color from 'styles/themes/wikifactory/styles/colors';
import { DfmProcessCutoffs, getDfmFeatureIcon } from 'apps/rfq/dfmConfig';
import { DfmSummaryItemStyled } from './dfmSummaryItem.styled';

export interface DfmFeatureProps {
  type: Object; // wasm dfm enum
  value: number; // cutoff for this feature
  text: string; // UI name for this feature
  test: string; // MAX || MIN
}

interface DfmSummaryItemProps {
  dfmFeatObj: DfmFeatureProps;
  dfmFeatType: number; // wasm dfm enum.value
  processId: string;
  units: string;
}

export const DfmSummaryItem = observer(
  ({ dfmFeatObj, dfmFeatType, processId, units }: DfmSummaryItemProps) => {
    if (!dfmFeatObj) return null;

    const viewerControl = useViewerControl();
    const dfmEnum = viewerControl?.viewer?.module.DfmAnalysisType;

    // feature cutoff for given processId
    const getFeatureCutoff = (type: Object) => {
      const cutoffs = new DfmProcessCutoffs(processId, units);
      if (cutoffs && !cutoffs.isEmpty) {
        return cutoffs.getFeatureCutoff(type, dfmEnum);
      }
      return null;
    };

    // construct the DFM feature UI object
    let { value } = dfmFeatObj;
    let isAngle = false;
    if (dfmFeatType === dfmEnum.DfmAnalysisSharpEdges.value) {
      value = (value * 180.0) / Math.PI;
      isAngle = true;
    }
    let roundFactor = 100;
    if (!isAngle) {
      if (units === 'cm') roundFactor = 1000;
      if (units === 'inch') roundFactor = 1000;
    }
    value = Math.round(value * roundFactor) / roundFactor;

    const dfmFeat = {
      value,
      text: dfmFeatObj.text,
      condition: dfmFeatObj.test,
      cutoff: getFeatureCutoff(dfmFeatType),
      isAngle,
    };

    /*
      events
    */

    const handleHover = () => {
      // highlight all warning items
      const settings = viewerControl?.viewer?.settings;
      settings?.DrawDfmWarnings(dfmFeatObj.type, false);
    };

    const handleMouseLeave = () => {
      // clear all highlighted warning items
      const selManager = viewerControl?.viewer?.selectionManager;
      selManager?.ClearHighlight();
    };

    /*
      render
    */

    const rValue = !dfmFeat.isAngle
      ? `${dfmFeat.value}`
      : `${dfmFeat.value.toFixed(0)}°`;

    const roundCutoff = dfmFeat.cutoff
      ? Math.round(dfmFeat.cutoff * roundFactor) / roundFactor
      : null;
    // eslint-disable-next-line no-nested-ternary
    const rCutoff = roundCutoff
      ? !dfmFeat.isAngle
        ? roundCutoff
        : `${roundCutoff}°`
      : null;

    const failed =
      rCutoff &&
      (dfmFeat.isAngle
        ? !(dfmFeat.cutoff >= dfmFeat.value)
        : !(dfmFeat.cutoff <= dfmFeat.value));

    let rSymbol = '=';
    if (dfmFeat.value > dfmFeat.cutoff) rSymbol = '>';
    else if (dfmFeat.value < dfmFeat.cutoff) rSymbol = '<';

    const rIcon = !failed ? (
      <SvgIcon icon={<Check />} size='small' color='primary' />
    ) : (
      <SvgIcon icon={<Warning />} size='small' />
    );

    const auxMsg = failed && 'see issues below';

    const icon = getDfmFeatureIcon(dfmFeatType, dfmEnum);

    return (
      <DfmSummaryItemStyled
        failed={failed}
        onMouseEnter={handleHover}
        onMouseLeave={handleMouseLeave}
      >
        {icon && <SvgIcon icon={icon} size='medium' />}
        <Box sx={{ color: color.wfBlack }}>{dfmFeat.text}</Box>
        <Box
          sx={{
            fontSize: '12px',
            textAlign: 'center',
            color: color.wfGrey800,
            fontFamily: 'monospace, monospace',
            border: `1px solid ${color.wfGrey200}`,
            background: color.wfGrey100,
            borderRadius: '5px',
            marginRight: '5px',
          }}
        >
          {dfmFeat.condition}
        </Box>
        <Box
          sx={{
            textAlign: 'center',
            fontWeight: '501',
          }}
        >
          {rValue}
        </Box>
        {dfmFeat.cutoff && (
          <>
            <Box
              sx={{
                color: color.wfGrey600,
                textAlign: 'center',
                fontSize: '16px',
                margin: '0 0.5rem 0 0',
              }}
            >
              {rSymbol}
            </Box>
            <Box
              sx={{
                color: color.wfGrey600,
                textAlign: 'center',
                border: `1px solid ${color.wfGrey400}`,
                borderRadius: '5px',
                fontWeight: '501',
              }}
            >
              {rCutoff}
            </Box>
            <Box sx={{ display: 'flex', marginLeft: '0.5rem' }}>{rIcon}</Box>
            <Box
              sx={{
                color: color.wfGrey500,
                margin: '0 0.5rem',
              }}
            >
              {auxMsg}
            </Box>
          </>
        )}
      </DfmSummaryItemStyled>
    );
  },
);
